import { isWorkHarnessChat } from '../../../contracts/projects/chatRouting';

import type { Heidi2GetStartedChecklistPorts } from './heidi-2-get-started-checklist-ports';

export interface Heidi2GetStartedProgress {
  hasGivenTask: boolean;
  hasConnectedApp: boolean;
  hasRunRoutine: boolean;
}

/**
 * Ticks the checklist from the lists each action already refreshes: sending
 * from Home creates a Work chat, connecting an app refetches the connections,
 * and running a routine refetches the runs. So an item ticks as it is done,
 * with no reload. Reads nothing while the checklist is hidden.
 *
 * A task only counts once sent after `since` (the welcome's first view), so
 * opening the composer ticks nothing and Work chats from before Heidi II do
 * not count; with no `since`, it never ticks. Apps and routines are standing
 * adoption, so any connection or run counts.
 */
export const useHeidi2GetStartedProgress = (
  ports: Heidi2GetStartedChecklistPorts,
  enabled: boolean,
  since: string | undefined
): Heidi2GetStartedProgress => {
  const isNangoEnabled = ports.useIsNangoIntegrationsEnabled();
  // Same default page as Recents, so the Home panel already has it cached.
  // Routine runs are excluded server-side, so running one does not tick this.
  const { data: chats } = ports.useInfiniteQueryChats({ enabled });
  const { data: connections } = ports.useQueryNangoConnections(enabled && isNangoEnabled);
  const { data: runs } = ports.useAllWorkflowRuns(undefined, { enabled });

  // Backend timestamps can be naive UTC (no `Z`); a bare `Date.parse` reads
  // those as local time, which would put a just-made item hours before `since`.
  const sinceDate = ports.parseTimeStampOrNull(since, true);
  const isAfterSince = (createdAt: string | null | undefined): boolean => {
    if (!sinceDate) return false;
    const created = ports.parseTimeStampOrNull(createdAt, true);
    return created !== null && created.getTime() >= sinceDate.getTime();
  };

  return {
    hasGivenTask:
      chats?.pages.some((page) =>
        page.chats.some(
          (chat) => isWorkHarnessChat(chat) && isAfterSince(chat.created_at)
        )
      ) ?? false,
    // A connection is a standing state, so one made before Heidi II counts.
    hasConnectedApp: (connections?.length ?? 0) > 0,
    // Scheduled runs land after the welcome on their own, so a cutoff would not
    // show the clinician ran one; any run counts, as with apps.
    hasRunRoutine: (runs?.runs.length ?? 0) > 0,
  };
};
